import React, { useState } from 'react'
import moment from 'moment'
import dayjs from 'dayjs'
import { Space, DatePicker, Input } from 'antd'
import { EditOutlined, PrinterOutlined, SearchOutlined, FilterOutlined } from '@ant-design/icons'
import Button from '../../components/common/button/button'
import InputComponent from '../../components/common/input/input'
import {
  handleGenerateLabelsUtil,
  saveLabelRequest,
  fetchRequestedItemsData
} from '../../api/shelfEdgeApiService/shelfEdgeLabellingApiUtil'

const { RangePicker } = DatePicker

export const formatDate = (text) => {
  if (text) {
    const dateObject = moment(text).local()
    const formattedDate = dateObject.format('DD/MM/YYYY')
    const formattedTime = dateObject.format('hh:mm A')
    return `${formattedDate} ${formattedTime}`
  }
  return ''
}

// Filter utility functions
export const getColumnSearchProps = (dataIndex, title) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <Input
        placeholder={`Search ${title}`}
        value={selectedKeys[0]}
        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm({ closeDropdown: true })}
        style={{ marginBottom: 8, display: 'block' }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: filtered => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!value || value === '') return true
    if (record[dataIndex] === null || record[dataIndex] === undefined) return false
    return record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
  }
})

export const getDateFilterProps = () => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }} onClick={(e) => e.stopPropagation()}>
      <RangePicker
        value={selectedKeys[0]}
        onChange={(dates) => {
          setSelectedKeys(dates ? [dates] : [])
        }}
        style={{ marginBottom: 8, display: 'block', width: '100%' }}
        format="DD/MM/YYYY"
        placeholder={['Start Date', 'End Date']}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          type="primary"
          onClick={() => confirm({ closeDropdown: true })}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90 }}
        >
          Search
        </Button>
        <Button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (clearFilters) clearFilters()
            setSelectedKeys([])
            confirm({ closeDropdown: true, clearFilters: true })
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </div>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    // Return true for all records when no date range is selected
    if (!value || !value[0] || !value[1]) {
      return true
    }

    // Get start and end dates from the date picker
    const startDate = value[0].startOf('day')
    const endDate = value[1].endOf('day')

    // Parse the record's date with dayjs
    const recordDate = dayjs(record.createdDate)

    // Check if the record's date is within the selected range (inclusive)
    return (
      (recordDate.isAfter(startDate) || recordDate.isSame(startDate)) &&
      (recordDate.isBefore(endDate) || recordDate.isSame(endDate))
    )
  }
})

export const getCustomFilterWithSearch = (dataIndex, options) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    const [searchText, setSearchText] = useState('')
    const filteredOptions = options.filter(option =>
      option.toString().toLowerCase().includes(searchText.toLowerCase())
    )

    return (
      <div style={{ padding: 16, background: '#fff', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: 240 }} onClick={(e) => e.stopPropagation()}>
        <Input
          placeholder={`Search ${dataIndex}...`}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          style={{ marginBottom: 8 }}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
        />
        <div style={{ maxHeight: '200px', overflow: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            {filteredOptions.map((option, index) => (
              <div key={option || `empty-${index}`} style={{ margin: '8px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={selectedKeys.includes(option)}
                    onChange={e => {
                      const newSelectedKeys = e.target.checked
                        ? [...selectedKeys, option]
                        : selectedKeys.filter(key => key !== option)
                      setSelectedKeys(newSelectedKeys)
                    }}
                    style={{ marginRight: 8 }}
                  />
                  <span>{option || '(Empty)'}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
          <Button
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (clearFilters) clearFilters()
              setSelectedKeys([])
              setSearchText('')
              confirm({ closeDropdown: true, clearFilters: true })
            }}
            size="small"
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm({ closeDropdown: true })}
            size="small"
          >
            OK
          </Button>
        </div>
      </div>
    )
  },
  filterIcon: (filtered) => (
    <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
  ),
  onFilter: (value, record) => {
    if (!value) return true
    if (record[dataIndex] == null) return value === null || value === ''
    return record[dataIndex].toString() === value.toString()
  }
})

export const validateProductData = (item, product) => {
  if (!product) {
    return false
  }

  // Validate APN
  if (!product.APN || isNaN(product.APN) || product.APN <= 0) {
    return false
  }

  // Validate ItemCode
  if (!product.ItemCode || isNaN(product.ItemCode) || product.ItemCode <= 0) {
    return false
  }

  // Validate ItemDescription
  if (!product.ItemDescription || product.ItemDescription.trim() === '') {
    return false
  }

  // Validate RecRetailIncPrice
  if (!product.ItemPrices?.ItemPrice?.RecRetailIncPrice ||
    isNaN(product.ItemPrices.ItemPrice.RecRetailIncPrice) ||
    product.ItemPrices.ItemPrice.RecRetailIncPrice <= 0) {
    return false
  }


  // Validate DepartmentCode first, then ReorderBuyerCode as fallback
  const departmentCode = product.DepartmentCode
  const reorderBuyerCode = product.ReorderBuyerCode

  // Check if DepartmentCode is valid
  const hasDepartmentCode = departmentCode !== null &&
    departmentCode !== undefined &&
    departmentCode !== '' &&
    departmentCode.toString().trim() !== ''

  // Check if ReorderBuyerCode is valid
  const hasReorderBuyerCode = reorderBuyerCode !== null &&
    reorderBuyerCode !== undefined &&
    reorderBuyerCode !== '' &&
    reorderBuyerCode.toString().trim() !== ''

  // If neither DepartmentCode nor ReorderBuyerCode is valid, item is invalid
  if (!hasDepartmentCode && !hasReorderBuyerCode) {
    return false
  }

  return true
}

export const getBuyerCode = (product) => {
  const departmentCode = product.DepartmentCode
  const reorderBuyerCode = product.ReorderBuyerCode

  // Check if DepartmentCode is valid
  const hasDepartmentCode = departmentCode !== null &&
    departmentCode !== undefined &&
    departmentCode !== '' &&
    departmentCode.toString().trim() !== ''

  if (hasDepartmentCode) {
    return departmentCode
  }

  // Fallback to ReorderBuyerCode
  return reorderBuyerCode
}

// Process print data and identify missing APNs with enhanced validation - returns object with both arrays
export const processPrintData = (printList, productData) => {
  const missingAPNsTemp = []
  const validProducts = []

  // Ensure printList is an array
  if (!Array.isArray(printList)) {
    return { missingAPNs: [], validProducts: [] }
  }

  printList.forEach((item) => {
    const apnNumber = parseInt(item.apn, 10)
    const product = productData.find((p) => p.APN === apnNumber)

    if (!validateProductData(item, product)) {
      missingAPNsTemp.push(item.apn)
    } else {
      validProducts.push(product)
    }
  })

  return { missingAPNs: missingAPNsTemp, validProducts }
}

// Export missing APNs as CSV with safety check
export const exportMissingAPNs = (missingAPNs) => {
  // Safety check to ensure missingAPNs is an array
  if (!Array.isArray(missingAPNs)) {
    console.error('missingAPNs is not an array:', missingAPNs)
    return
  }

  const csvContent = 'data:text/csv;charset=utf-8,' +
    missingAPNs.map(apn => `"=""${apn}"""`).join('\n')
  const encodedUri = encodeURI(csvContent)
  const link = document.createElement('a')
  link.setAttribute('href', encodedUri)
  link.setAttribute('download', 'Invalid Apns.csv')
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// Enhanced print function with validation for all users
export const handlePrint = async (printrow, instance, setIsReloading, setProductData, setFlag, setPrintList, messageApi, setMissingAPNs) => {
  setPrintList(printrow.printList)
  try {
    setIsReloading(true)

    const productInfo = await handleGenerateLabelsUtil(
      instance,
      setProductData,
      printrow.printList
    )

    if (productInfo?.status === 200) {
      // Process the data with enhanced validation
      const { missingAPNs, validProducts } = processPrintData(printrow.printList, productInfo.data.productInfo)

      // Set missing APNs for display (with safety check)
      if (setMissingAPNs && typeof setMissingAPNs === 'function') {
        setMissingAPNs(Array.isArray(missingAPNs) ? missingAPNs : [])
      }

      // Check if we have valid products
      if (validProducts.length === 0) {
        messageApi.error('No valid products found for printing. Please check the APN list.')
        return false
      }

      // Show warning if some APNs are invalid but allow printing valid ones
      if (missingAPNs.length > 0) {
        messageApi.warning(`${missingAPNs.length} invalid APN(s) found. Only valid products will be printed.`)
      }

      // Set only valid product data
      setProductData(validProducts)
      setFlag(true)
      return true
    } else {
      messageApi.error('Failed to retrieve product information')
      return false
    }
  } catch (error) {
    console.error('Error in handlePrint:', error)
    messageApi.error('Failed to generate labels')
    return false
  } finally {
    setIsReloading(false)
  }
}

// Handle saving edits to saved labels
export const handleSaveEdit = async (formEditRow, editRowData, setLoading, setIsReloading, instance, setEditModalVisible,
  messageApi, fetchData) => {
  try {
    await formEditRow.validateFields()

    if (!editRowData.guid) {
      return false
    }

    const params = {
      id: editRowData.id,
      listName: editRowData.listName,
      printList: editRowData.printList || [],
      createdBy: editRowData.createdBy,
      createdDate: new Date().toISOString(),
      requestType: 'update'
    }

    setLoading(true)
    setIsReloading(true)

    const response = await saveLabelRequest(params, instance, 'update')

    if (response && (response.status === 200 || response.status === 204)) {
      setEditModalVisible(false)
      messageApi.success('Saved shelf edge labels updated!')
      await fetchData()
      return true
    } else {
      throw new Error('Update request did not return a successful response')
    }
  } catch {
    messageApi.error('Failed to update saved shelf edge labels')
    return false
  } finally {
    setLoading(false)
    setIsReloading(false)
  }
}

// Validate edit changes
export const validateEditChange = (field, value, messageApi) => {
  if (field === 'quantity') {
    const updatedValue = parseInt(value, 10)
    if (isNaN(updatedValue) || updatedValue < 1) {
      messageApi.error('Quantity cannot be less than 1!')
      return false
    }
  }

  if (field === 'apn' && !value) {
    messageApi.error('APN cannot be empty!')
    return false
  }

  return true
}

// Get table columns for the main saved labels table
export const getSavedLabelsColumns = (handleMainEdit, handlePrintClick, isReloading) => {
  return [
    {
      title: 'Request Name',
      dataIndex: 'listName',
      key: 'listName',
      ellipsis: true
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      ellipsis: true
    },
    {
      title: 'Created Date',
      dataIndex: 'createdDate',
      key: 'createdDate',
      render: (text) => formatDate(text)
    },
    {
      title: 'Expires At',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (text) => text ? moment.unix(text).local().format('DD/MM/YYYY hh:mm A') : ''
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleMainEdit(record)}
            type="text"
            tooltip="Edit"
          />
          <Button
            icon={<PrinterOutlined />}
            onClick={() => handlePrintClick(record)}
            disabled={isReloading}
            type="text"
            tooltip="Print"
          />
        </Space>
      )
    }
  ]
}

// Get edit columns for the edit modal
export const getEditColumns = (handleEditTableChange) => {
  return [
    {
      title: 'APN',
      dataIndex: 'apn',
      key: 'apn',
      render: (text, record) => (
        <InputComponent
          defaultValue={text}
          onChange={(e) => handleEditTableChange(e.target.value, record, 'apn')}
        />
      )
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (text, record) => (
        <InputComponent
          type="number"
          min={1}
          defaultValue={text}
          onChange={(e) => handleEditTableChange(e.target.value, record, 'quantity')}
        />
      )
    }
  ]
}

// Get missing APNs columns
export const getMissingAPNColumns = () => {
  return [
    {
      title: 'No.',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Invalid APN',
      dataIndex: 'apn',
      key: 'apn'
    }
  ]
}

// Create page style for printing
export const createPageStyle = (selectedPrinter) => {
  return `
    @media print {
      @page {
        size: ${selectedPrinter === 'Zebra' ? '7cm 4cm' : 'A4'};
        margin: 0;
      }
      body {
        -webkit-print-color-adjust: exact;
      }
      
      /* Hide everything except print content */
      body * {
        visibility: hidden;
      }
      
      .print-only-content,
      .print-only-content * {
        visibility: visible !important;
      }
      
      /* Position the print content */
      .print-only-content {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
      }
      
      /* Force page breaks for Zebra printer */
      ${selectedPrinter === 'Zebra' ? `
        .zebra-label, 
        .print-preview__Zebra-container {
          page-break-after: always !important;
          page-break-inside: avoid !important;
        }
      ` : ''}
    }
  `
}

// Fetch data utility function
export const fetchDataUtil = async (instance, setRows, setIsReloading, messageApi) => {
  try {
    await fetchRequestedItemsData(instance, setRows, setIsReloading)
  } catch {
    messageApi.error('Failed to load saved labels')
  }
}

// Handle deleting selected saved items
export const handleDeleteSavedItems = async (
  selectedRows,
  setLoading,
  setIsReloading,
  instance,
  setRows,
  setSelectedRows,
  setDeleteModalVisible,
  messageApi
) => {
  try {
    setLoading(true)
    setIsReloading(true)

    const idsToDelete = selectedRows.map((item) => item.id)

    const data = {
      id: idsToDelete
    }

    const response = await saveLabelRequest(data, instance, 'delete')

    // Check for success
    if (response && (response.status === 200 || response.status === 201 || response.status === 204)) {
      setRows(prevRows => prevRows.filter(row => !idsToDelete.includes(row.id)))
      setSelectedRows([])
      setDeleteModalVisible(false)
      messageApi.success('Deleted successfully!')
    } else {
      messageApi.error('Failed to delete labels')
    }
  } catch {
    messageApi.error('An unexpected error occurred during deletion')
  } finally {
    setLoading(false)
    setIsReloading(false)
  }
}